'use client';

// NOTE: Next.js App Router requires error.tsx/global-error.tsx to use a
// default export. The "named exports only" project convention does not apply
// to these framework-mandated entry points.

import { useEffect } from 'react';
import { isApiError } from './lib/errors/api-error';
import { Button } from './ui/button';

type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function RouteError({ error, reset }: Props) {
  useEffect(() => {
    console.error('Route error', error);
  }, [error]);

  // STORE_ACCESS_DENIED / UNAUTHORIZED arrive here via QueryClient.throwOnError.
  const apiError = isApiError(error) ? error : null;
  const isAccessDenied = apiError?.code === 'STORE_ACCESS_DENIED';
  const isForbidden = apiError?.code === 'UNAUTHORIZED';

  let title = 'This page hit an error';
  let body = 'Try again or go back to the previous page.';
  if (isAccessDenied) {
    title = "You don't have access to this store";
    body = 'Pick a different store from your home page.';
  } else if (isForbidden) {
    title = "You don't have permission";
    body = 'Contact your admin if you think this is wrong.';
  }

  const requestId = apiError?.requestId ?? error.digest;

  return (
    <main className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{body}</p>
        {requestId ? (
          <p className="font-mono text-xs text-muted-foreground">Error ID: {requestId}</p>
        ) : null}
        <div className="flex justify-center gap-2">
          {!isAccessDenied && !isForbidden ? <Button onClick={reset}>Try again</Button> : null}
          <Button variant="outline" onClick={() => window.location.assign('/')}>
            Go home
          </Button>
        </div>
      </div>
    </main>
  );
}
